import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import type SlideModel from '@src/slide/domain/ui-model/element-modeles/slide-model';
import { INoteSlideData, ISlideDataModel, NoteSlideDataModel } from '@tencent/slide-sdk-model';
import {
    ICopiedLayoutData,
    ICopiedMasterData,
    ICopiedNoteSlideData,
    ICopiedThemeData,
} from '@src/slide/domain/ui-model/element-modeles/property/copy/copy-model/paste-special.interface';
import cloneDeep from 'lodash/cloneDeep';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IThemeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';

export default class CopySpecialUtils {
    /**
     * 获取幻灯片的备注数据
     * @param selectedArray UIModel[]
     * @returns ICopiedNoteSlideData[] getCopiedNoteSdkData
     */
    public static getCopiedNoteSdkData(selectedArray: SlideModel[]): ICopiedNoteSlideData[] {
        const arr: ICopiedNoteSlideData[] = [];
        selectedArray.forEach((slideModel) => {
            if (slideModel.type !== UIModelType.SLIDE) return;
            if (!slideModel.noteData) return;
            const noteDataModel = (slideModel.sdkData as ISlideDataModel)?.getNoteSlide() as NoteSlideDataModel;
            if (!noteDataModel) return;
            arr.push({
                inheritedSdkData: noteDataModel.getInheritedData() as INoteSlideData,
                ownSdkData: noteDataModel.getOwnedData() as INoteSlideData,
            });
        });
        return cloneDeep(arr);
    }

    /**
     * 获取需要复制的母版UI数据（需去重）
     * @returns IMasterModel[]
     */
    public static getNeedCopiedMasterList(selectedArray: ISlideModel[], masterList: IMasterModel[]): IMasterModel[] {
        if (!selectedArray.length) return [];
        const masterIdSet = new Set();
        selectedArray.forEach((slideModel) => {
            if (slideModel.type !== UIModelType.SLIDE) return;
            const masterId = slideModel.getMasterId();
            masterIdSet.add(masterId);
        });

        return masterList.filter(masterModel => masterIdSet.has(masterModel.id));
    }

    /**
     * 获取需要复制的母版sdk数据
     * @returns ICopiedMasterData[]
     */
    public static getCopiedMasterSdkData(masterList: IMasterModel[]): ICopiedMasterData[] {
        return masterList.map((masterModel) => {
            const handler = masterModel.copyModelHandler();
            return handler.getCopiedSdkModel({ withoutInheritedData: true });
        }) as unknown as ICopiedMasterData[];
    }

    /**
     * 获取需要复制的所有版式sdk数据
     * @returns ILayoutDataModel[]
     */
    public static getCopiedLayoutSdkData(masterList: IMasterModel[]): ICopiedLayoutData[] {
        let layoutSdkDataList: ICopiedLayoutData[] = [];
        masterList.forEach((masterModel: IMasterModel) => {
            const list = masterModel.layoutList.map(layoutModel =>
                layoutModel.copyModelHandler().getCopiedSdkModel({ withoutInheritedData: true }));
            layoutSdkDataList = [...layoutSdkDataList, ...list];
        });

        return layoutSdkDataList;
    }

    /**
     * 获取需要复制的主题sdk数据
     * @param masterList 母版
     * @returns ICopiedThemeData[]
     */
    public static getCopiedThemeSdkData(masterList: IMasterModel[]): ICopiedThemeData[] {
        const themeSdkDataList: ICopiedThemeData[] = [];
        const themeModelList: IThemeModel[] = [];
        masterList.forEach((masterModel: IMasterModel) => {
            const themeModel = masterModel.getTheme();
            // 剔除重复主题
            if (themeModel && !themeModelList.includes(themeModel)) {
                themeModelList.push(themeModel);
                const copiedThemeModel = themeModel.copyModelHandler()
                    .getCopiedSdkModel({ withoutInheritedData: true }) as unknown as ICopiedThemeData;
                themeSdkDataList.push(copiedThemeModel);
            }
        });

        return themeSdkDataList;
    }
}
